import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Target, Save, Info, CalendarDays, TrendingUp, MapPin } from 'lucide-react';

const COUNT_KPIS = new Set([
  'fa_presented','fa_sold_total','fa_sold_same_day','fa_sold_previous',
  'arches_total','arches_same_day','arches_previous',
  'consults_scheduled','consults_showed','inbound_calls',
  'total_revenue','collections_eod','surgical_production','total_collected'
]);

const GOAL_GROUPS = [
  {
    title: 'Full Arch Performance',
    color: 'hsl(191,95%,43%)',
    keys: [
      { key: 'fa_presented', label: 'FA Presented', hint: 'Monthly target for FA consults presented', suffix: '' },
      { key: 'fa_sold_total', label: 'FA Sold (Total)', hint: 'Total FA cases closed per month', suffix: '' },
      { key: 'fa_sold_same_day', label: 'FA Sold (Same Day)', hint: 'Same-day closes per month', suffix: '' },
      { key: 'fa_sold_previous', label: 'FA Sold (Previous)', hint: 'From previous consults per month', suffix: '' },
      { key: 'arches_total', label: 'Arches Sold', hint: 'Total arches per month', suffix: '' },
      { key: 'same_day_close_rate', label: 'Same Day Close Rate', hint: 'Target % — no proration', suffix: '%' },
      { key: 'normalized_close_rate', label: 'Normalized Close Rate', hint: 'Target % — no proration', suffix: '%' },
    ]
  },
  {
    title: 'Consult Funnel',
    color: '#3B82F6',
    keys: [
      { key: 'consults_scheduled', label: 'Consults Scheduled', hint: 'Monthly consult bookings target', suffix: '' },
      { key: 'consults_showed', label: 'Consults Showed', hint: 'Monthly shows target', suffix: '' },
      { key: 'show_rate', label: 'Show Rate', hint: 'Target % — no proration', suffix: '%' },
      { key: 'inbound_calls', label: 'Inbound Calls', hint: 'Monthly call volume target', suffix: '' },
    ]
  },
  {
    title: 'Revenue',
    color: '#16A34A',
    keys: [
      { key: 'total_revenue', label: 'Total Revenue (Sales)', hint: 'Monthly revenue from sales', suffix: '$', prefix: '$' },
      { key: 'collections_eod', label: 'EOD Collections', hint: 'Monthly collections target', suffix: '$', prefix: '$' },
      { key: 'surgical_production', label: 'Surgical Production', hint: 'Monthly surgical production', suffix: '$', prefix: '$' },
    ]
  },
  {
    title: 'Speed Metrics',
    color: '#8B5CF6',
    keys: [
      { key: 'avg_consult_to_close_days', label: 'Avg Days Consult→Close', hint: 'Target max days (lower is better)', suffix: 'd' },
      { key: 'avg_close_to_treatment_days', label: 'Avg Days Close→Treatment', hint: 'Target max days (lower is better)', suffix: 'd' },
    ]
  }
];

const GoalsForm = ({ locationId, locationName, saving, onSave }) => {
  const [goals, setGoals] = useState({});
  const [localGoals, setLocalGoals] = useState({});
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!locationId) { setLoading(false); return; }
    setLoading(true);
    axios.get(`${API}/goals`, { params: { location_id: locationId } })
      .then(r => { setGoals(r.data.goals || {}); setLocalGoals(r.data.goals || {}); })
      .catch(err => console.error('[Goals] Load error:', err))
      .finally(() => setLoading(false));
  }, [locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, val) => {
    setLocalGoals(prev => ({ ...prev, [key]: val === '' ? null : (isNaN(parseFloat(val)) ? null : parseFloat(val)) }));
    setDirty(true);
  };

  const handleSave = async () => {
    // locationId is ALWAYS required — passed from parent GoalsPage
    await onSave(localGoals, locationId);
    setGoals(localGoals);
    setDirty(false);
  };

  if (loading) return <div className="text-slate-400 text-sm p-4">Loading goals...</div>;

  return (
    <div className="space-y-6">
      {GOAL_GROUPS.map(group => (
        <Card key={group.title} className="bg-white shadow-sm" style={{ borderTop: `3px solid ${group.color}` }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <TrendingUp size={14} style={{ color: group.color }} />
              {group.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {group.keys.map(({ key, label, hint, suffix, prefix }) => {
                const isCount = COUNT_KPIS.has(key);
                const val = localGoals[key];
                return (
                  <div key={key} className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-sm font-medium text-slate-700">{label}</Label>
                      <Badge className={`text-xs border-0 py-0 px-1.5 ${isCount ? 'bg-cyan-100 text-cyan-700' : 'bg-purple-100 text-purple-700'}`}>
                        {isCount ? 'pro-rated' : 'fixed %'}
                      </Badge>
                    </div>
                    <div className="relative">
                      {prefix && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">{prefix}</span>
                      )}
                      <Input
                        type="number" min="0" step={suffix === '%' || suffix === 'd' ? '0.1' : '1'}
                        value={val === null || val === undefined ? '' : val}
                        onChange={e => set(key, e.target.value)}
                        placeholder={`Monthly target${suffix ? ` (${suffix})` : ''}`}
                        className={`h-9 text-sm ${prefix ? 'pl-7' : ''}`}
                      />
                      {suffix && !prefix && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">{suffix}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">{hint}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
      <div className="pb-6">
        <Button onClick={handleSave} disabled={saving || !dirty}
          className="w-full h-11 font-semibold gap-2"
          style={{ background: 'hsl(191,95%,43%)', color: 'hsl(222,47%,11%)' }}>
          <Save size={15} /> {saving ? 'Saving...' : `Save Goals${locationName ? ` for ${locationName}` : ''}`}
        </Button>
      </div>
    </div>
  );
};

export default function GoalsPage() {
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('');

  useEffect(() => {
    axios.get(`${API}/locations`)
      .then(r => {
        const locs = r.data.filter(l => l.active);
        setLocations(locs);
        if (locs.length > 0) setActiveTab(locs[0].id);
      })
      .catch(err => console.error('[Goals] Load error:', err));
  }, []);

  const handleSave = async (goalData, locationId) => {
    if (!locationId) {
      toast.error('No location selected');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/goals`, { goals: goalData }, { params: { location_id: locationId } });
      toast.success('Goals saved for this location!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save goals');
    }
    setSaving(false);
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <Target size={20} className="text-blue-600" /> Monthly Goals
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Set monthly targets per location. The dashboard automatically pro-rates counts to the selected date range and sums all locations when viewing overall performance.
        </p>
      </div>

      {/* Info banner */}
      <div className="surface-card px-4 py-3 flex items-start gap-2.5">
        <CalendarDays size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-slate-600 space-y-0.5">
          <p><strong className="text-slate-800">Count KPIs</strong> (FA Presented, Arches, etc.) are pro-rated: <code className="bg-blue-50 text-blue-700 px-1 rounded">goal × days ÷ 30.44</code></p>
          <p><strong className="text-slate-800">Rate KPIs</strong> (%, days) use the same target regardless of period.</p>
          {locations.length > 1 && (
            <p className="text-blue-700 font-medium">Multiple locations: goals are set per location and automatically <strong>summed</strong> on the dashboard when no location filter is active.</p>
          )}
        </div>
      </div>

      {locations.length === 0 ? (
        <div className="surface-card p-8 text-center">
          <MapPin className="mx-auto mb-2 text-slate-300" size={32} />
          <p className="text-slate-600 font-medium">No active locations</p>
          <p className="text-slate-400 text-sm mt-1">Add a location first to set goals</p>
        </div>
      ) : locations.length === 1 ? (
        // Single location — just show the form directly, always save with that location's ID
        <>
          <div className="flex items-center gap-2 p-3 rounded-lg surface-card mb-2">
            <MapPin size={14} className="text-blue-600" />
            <p className="text-sm text-slate-600">
              Goals for <strong className="text-slate-800">{locations[0].name}</strong>
            </p>
          </div>
          <GoalsForm locationId={locations[0].id} locationName={locations[0].name} saving={saving} onSave={handleSave} />
        </>
      ) : (
        // Multiple locations — tab per location
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 border border-slate-200 flex-wrap h-auto gap-1 p-1 rounded-xl">
            {locations.map(loc => (
              <TabsTrigger key={loc.id} value={loc.id}
                className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-500 text-xs gap-1 rounded-lg px-3 py-1.5">
                <MapPin size={11} /> {loc.name}
              </TabsTrigger>
            ))}
          </TabsList>
          {locations.map(loc => (
            <TabsContent key={loc.id} value={loc.id} className="mt-4">
              <div className="flex items-start gap-2 p-3 rounded-lg surface-card mb-4">
                <MapPin size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-600">
                  Goals for <strong className="text-slate-800">{loc.name}</strong> —
                  when the dashboard is filtered to this location, these goals apply.
                  <span className="text-blue-600 font-medium"> All location goals are summed for the overall dashboard view.</span>
                </p>
              </div>
              <GoalsForm locationId={loc.id} locationName={loc.name} saving={saving} onSave={handleSave} />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
